import { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';

// Note: Capacitor Biometric Auth would require @capacitor/local-authentication
// For now we use a web-based approach with WebAuthn where supported

export function useBiometric() {
  const [isAvailable, setIsAvailable] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const biometricEnabled = useAuthStore((s) => s.biometricEnabled);
  const setBiometricEnabled = useAuthStore((s) => s.setBiometricEnabled);

  useEffect(() => {
    const checkAvailability = async () => {
      try {
        // Check if WebAuthn is available (Face ID / Touch ID on iOS Safari, Windows Hello, etc.)
        const available =
          typeof window !== 'undefined' &&
          window.PublicKeyCredential !== undefined &&
          typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function';

        if (available) {
          const result = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
          setIsAvailable(result);
        } else {
          setIsAvailable(false);
        }
      } catch {
        setIsAvailable(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkAvailability();
  }, []);

  const promptBiometric = useCallback(async (): Promise<boolean> => {
    if (!isAvailable) return false;

    try {
      // Use WebAuthn for biometric prompt
      const challenge = new Uint8Array(32);
      window.crypto.getRandomValues(challenge);

      const publicKey: PublicKeyCredentialRequestOptions = {
        challenge,
        rpId: window.location.hostname,
        userVerification: 'required',
        timeout: 60000,
      };

      await navigator.credentials.get({ publicKey });
      return true;
    } catch {
      return false;
    }
  }, [isAvailable]);

  const enableBiometric = useCallback(() => {
    setBiometricEnabled(true);
  }, [setBiometricEnabled]);

  const disableBiometric = useCallback(() => {
    setBiometricEnabled(false);
  }, [setBiometricEnabled]);

  return {
    isAvailable,
    isChecking,
    isEnabled: biometricEnabled,
    promptBiometric,
    enableBiometric,
    disableBiometric,
  };
}
